import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { Link, useParams } from "react-router-dom";
import {
  type AlignmentItem,
  type BenchmarkCohort,
  type BenchmarkRollup,
  type CostOpportunity,
  type CostRisk,
  type EpcCostRow,
  type FollowupQuestion,
  type PricingAssessment,
  type PricingRag,
  type ProcurementAssessmentPayload,
  type ProcurementFindings,
  type ProcurementGetResult,
  type ProcurementInputChoice,
  type ProcurementInputKind,
  type ProcurementInputs,
  type ProcurementInputState,
  PROCUREMENT_INPUT_LABEL,
  describeProcurementInput,
  procurementInputsReady,
  useChooseProcurementInput,
  isOperationFinished,
  useAsyncOperation,
  useDismissProcurementFinding,
  useProcurementAssessment,
  useProject,
  useRunProcurementAssessment,
  ViewAiTraceButton,
} from "../api";
import { QK } from "../api/queryKeys";
import { type CurrencyCode, currencyCodeOf, currencySymbol } from "../utils/currency";
import { projectDisplayName } from "../utils/projectName";
import { loadXlsxStyle } from "../utils/xlsxStyle";
import { DemoBlur } from "../components/DemoBlur";
import { SightingList } from "../components/SightingList";
import { SourceTextModal } from "../components/SourceTextModal";
import styles from "./ProjectProcurementTab.module.css";

const PROCUREMENT_QUERY_KEY = QK.procurement;

const RAG_LABEL: Record<PricingRag, string> = {
  green: "On target",
  amber: "Above target",
  red: "Materially above target",
  unknown: "No target available",
};

const RAG_CLASS: Record<PricingRag, string> = {
  green: styles.ragGreen,
  amber: styles.ragAmber,
  red: styles.ragRed,
  unknown: styles.ragUnknown,
};

const STALE_REASON_LABEL: Record<string, string> = {
  epc_cost_upload_replaced: "a different EPC cost spreadsheet is now used",
  on_site_report_upload_replaced: "a different on-site survey is now used",
  epc_cost_changed: "the EPC cost spreadsheet changed",
  on_site_report_changed: "the on-site survey changed",
  epc_cost_upload_removed: "the EPC cost spreadsheet was removed",
  on_site_report_upload_removed: "the on-site survey was removed",
};

// Currency is a per-project display concern (UK sites are £, Netherlands
// sites are €), driven by the project's ``currency_code`` on the payload.
// The costs themselves are already in the project's currency - this is a
// pure symbol/format swap, never a conversion. Everything on the tab that
// renders money reads its formatters off ``CurrencyContext`` so a single
// value at the top drives every card, the EPC summary, and the xlsx export.
// The symbol mapping is shared with the rest of the app via utils/currency.
interface Currency {
  code: CurrencyCode;
  symbol: string;
  // Intl currency format (no decimals) - the headline totals.
  money: (n: number | null | undefined) => string;
  // "<symbol>N / kWp" - the per-kWp figures.
  perKwp: (n: number | null | undefined) => string;
  // "<symbol>N" - the compact per-kWp cells in the benchmark table.
  perKwpShort: (value: number | null) => string;
}

const makeCurrency = (code: string | null | undefined): Currency => {
  const c = currencyCodeOf(code);
  const symbol = currencySymbol(code);
  return {
    code: c,
    symbol,
    money: (n) => {
      if (n === null || n === undefined) return "—";
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: c,
        maximumFractionDigits: 0,
      }).format(n);
    },
    perKwp: (n) => {
      if (n === null || n === undefined) return "—";
      return `${symbol}${Math.round(n).toLocaleString("en-GB")} / kWp`;
    },
    perKwpShort: (value) =>
      value !== null && Number.isFinite(value)
        ? `${symbol}${Math.round(value).toLocaleString("en-GB")}`
        : "—",
  };
};

// Default to GBP so any consumer rendered outside a provider (and every
// pre-existing UK assessment) keeps the £ behaviour unchanged.
const CurrencyContext = createContext<Currency>(makeCurrency("GBP"));

const useCurrency = (): Currency => useContext(CurrencyContext);

// Match rows to findings by label. Findings cite cost rows via
// ``cited_cost_rows`` (a list of row labels). A finding belongs to a
// row when the row's label is in that list. Case-insensitive +
// whitespace-tolerant so an LLM that produced "scaffolding" still
// matches a row labelled "Scaffolding".
const matchesRow = (rowLabel: string, citedRows: string[]): boolean => {
  const target = rowLabel.trim().toLowerCase();
  return citedRows.some((c) => c.trim().toLowerCase() === target);
};

// Dismissal envelope attached to every finding before it's grouped.
// The operator can dismiss any single finding (alignment / followup /
// risk / opportunity); dismissed items are filtered out of the EPC
// summary text + xlsx and out of the worst/best-case bracket. The
// per-row card renders them struck-through with a Reinstate
// affordance instead of dropping them - dismissals are reversible.
//
// ``_kind`` + ``_index`` identify the position in the original
// ``findings.epc_followup_questions`` / ``cost_risks`` / etc. arrays,
// which is what the BE's
// ``POST /projects/procurement/finding-dismissal`` keys on.
type FindingKind = "followup" | "risk" | "opportunity" | "alignment";
interface DismissalMeta {
  _kind: FindingKind;
  _index: number;
  _dismissal: { by: string; at: string } | null;
  // Softer toggle than _dismissal: hides from EPC text/xlsx but stays
  // visible on the platform. See ProcurementAssessment.epc_hidden.
  _epcHidden: { by: string; at: string } | null;
}
type MarkedFollowup = FollowupQuestion & DismissalMeta;
type MarkedRisk = CostRisk & DismissalMeta;
type MarkedOpportunity = CostOpportunity & DismissalMeta;
type MarkedAlignment = AlignmentItem & DismissalMeta;

interface PerRowFindings {
  followups: MarkedFollowup[];
  risks: MarkedRisk[];
  opportunities: MarkedOpportunity[];
  alignments: MarkedAlignment[];
}

const lookupDismissal = (
  dismissals:
    | ProcurementFindings["dismissals"]
    | ProcurementFindings["epc_hidden"]
    | undefined,
  kind: FindingKind,
  index: number,
): { by: string; at: string } | null => {
  const map = dismissals ?? {};
  const entry = map[`${kind}:${index}`];
  return entry ?? null;
};

const groupFindingsByRow = (
  rows: EpcCostRow[],
  findings: ProcurementFindings,
): {
  byRow: Map<string, PerRowFindings>;
  unmatched: PerRowFindings;
} => {
  const byRow = new Map<string, PerRowFindings>();
  for (const row of rows) {
    byRow.set(row.label, {
      followups: [],
      risks: [],
      opportunities: [],
      alignments: [],
    });
  }
  const unmatched: PerRowFindings = {
    followups: [],
    risks: [],
    opportunities: [],
    alignments: [],
  };
  const place = <T extends { cited_cost_rows: string[] } & DismissalMeta>(
    item: T,
    target: "followups" | "risks" | "opportunities" | "alignments",
  ) => {
    let placed = false;
    for (const row of rows) {
      if (matchesRow(row.label, item.cited_cost_rows)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (byRow.get(row.label)![target] as any[]).push(item);
        placed = true;
      }
    }
    if (!placed) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (unmatched[target] as any[]).push(item);
    }
  };
  findings.epc_followup_questions.forEach((f, i) =>
    place(
      {
        ...f,
        _kind: "followup",
        _index: i,
        _dismissal: lookupDismissal(findings.dismissals, "followup", i),
        _epcHidden: lookupDismissal(findings.epc_hidden, "followup", i),
      },
      "followups",
    ),
  );
  findings.cost_risks.forEach((r, i) =>
    place(
      {
        ...r,
        _kind: "risk",
        _index: i,
        _dismissal: lookupDismissal(findings.dismissals, "risk", i),
        _epcHidden: lookupDismissal(findings.epc_hidden, "risk", i),
      },
      "risks",
    ),
  );
  findings.cost_opportunities.forEach((o, i) =>
    place(
      {
        ...o,
        _kind: "opportunity",
        _index: i,
        _dismissal: lookupDismissal(findings.dismissals, "opportunity", i),
        _epcHidden: lookupDismissal(findings.epc_hidden, "opportunity", i),
      },
      "opportunities",
    ),
  );
  findings.alignment_items.forEach((a, i) =>
    place(
      {
        ...a,
        _kind: "alignment",
        _index: i,
        _dismissal: lookupDismissal(findings.dismissals, "alignment", i),
        _epcHidden: lookupDismissal(findings.epc_hidden, "alignment", i),
      },
      "alignments",
    ),
  );
  return { byRow, unmatched };
};

export const ProjectProcurementTab = () => {
  const { projectId } = useParams();
  const decoded = projectId ? decodeURIComponent(projectId) : "";
  const { data, isLoading } = useProcurementAssessment(decoded || undefined);
  const { data: project } = useProject(decoded);
  const projectName = project ? projectDisplayName(project) : decoded;
  const runProcurement = useRunProcurementAssessment();
  const [runError, setRunError] = useState<string | null>(null);

  // The /projects/procurement/run endpoint returns immediately with an
  // operation_key. We hold that key in state and useAsyncOperation
  // polls /async_status every 3s until the LRO completes. On
  // completion we invalidate the procurement-get query so the new
  // assessment is loaded; on failure we surface the worker's error
  // message rather than the no-op silent state operators saw before.
  const [operationKey, setOperationKey] = useState<string | null>(null);
  // Whether the current in-flight run was started by the auto-trigger
  // (both files extracted) rather than a user click. Drives the
  // progress copy ("Running automatically..." vs "Re-running...") and
  // keeps the run-button disabled state honest after a tab navigation.
  const [autoTriggered, setAutoTriggered] = useState(false);
  const { data: operation } = useAsyncOperation(operationKey, {
    enabled: Boolean(operationKey),
  });
  const qc = useQueryClient();

  // Resume polling on mount / data refresh when the BE tells us a
  // procurement LRO is already in flight for this project. Without
  // this, navigating away from the tab while the auto-trigger is
  // running drops the polling state and the tab looks idle when the
  // worker is actually busy.
  const pendingKey =
    data && "pending_operation_key" in data
      ? (data as { pending_operation_key?: string | null }).pending_operation_key
      : null;
  useEffect(() => {
    if (operationKey || !pendingKey) return;
    setOperationKey(pendingKey);
    setAutoTriggered(true);
  }, [pendingKey, operationKey]);

  useEffect(() => {
    if (!operationKey || !operation) return;
    if (operation.status === "completed") {
      qc.invalidateQueries({ queryKey: [PROCUREMENT_QUERY_KEY] });
      setOperationKey(null);
      setAutoTriggered(false);
    } else if (isOperationFinished(operation.status)) {
      setRunError(
        operation.error ??
          "The procurement assessment failed without a specific error message.",
      );
      setOperationKey(null);
      setAutoTriggered(false);
    }
  }, [operation, operationKey, qc]);

  const isRunning = Boolean(operationKey) || runProcurement.isLoading;
  const runningStage = !operationKey
    ? "Submitting…"
    : operation?.status === "running"
      ? autoTriggered
        ? "Running automatically now that both files are extracted (typically 30-90 seconds)…"
        : "Running cost-vs-survey alignment on Bedrock (typically 30-90 seconds)…"
      : autoTriggered
        ? "Both files are extracted - queuing the procurement assessment…"
        : "Waiting for the agent worker to pick up the job…";

  const onRun = async () => {
    if (!decoded) return;
    setRunError(null);
    try {
      const response = await runProcurement.mutateAsync({ project_id: decoded });
      // The BE dedups against any in-flight LRO and returns the
      // existing key when one's already running, so this is safe to
      // call even when the auto-trigger is mid-run - the polling
      // attaches to the same worker rather than orphaning it.
      setOperationKey(response.operation_key);
      // A click means the user is asking for it - drop the auto-trigger
      // copy in favour of the manual run copy.
      setAutoTriggered(false);
    } catch (err) {
      setRunError(err instanceof Error ? err.message : String(err));
    }
  };

  // Per-finding dismiss / reinstate, posted to the BE which mutates
  // the assessment's JSONB ``findings.dismissals`` map. The mutation
  // hook seeds the procurement-get cache with the BE response on
  // success, so the tab re-renders without a refetch round-trip.
  const dismissFinding = useDismissProcurementFinding();
  const { user: kindeUser } = useKindeAuth();
  const assessmentId =
    data && (data.status === "fresh" || data.status === "stale")
      ? data.assessment_id
      : null;
  // Resolve the actor display name on the FE so the dismissal stamp
  // reads as a human-readable identity (``noaa@inrange.io``,
  // ``Noaa Cohn``, ...) rather than the opaque Kinde sub the BE
  // would otherwise persist when the JWT carries no email claim.
  // Mirrors the ``pulled_by_name`` pattern used by Gmail / Drive
  // imports.
  const actorName =
    [kindeUser?.given_name, kindeUser?.family_name].filter(Boolean).join(" ") ||
    kindeUser?.email ||
    undefined;
  const onToggleDismiss = async (
    kind: FindingKind,
    index: number,
    dismissed: boolean,
    scope: "dismiss" | "epc_only" = "dismiss",
  ) => {
    if (!assessmentId) return;
    try {
      await dismissFinding.mutateAsync({
        assessment_id: assessmentId,
        kind,
        index,
        dismissed,
        scope,
        actor_name: actorName,
      });
    } catch (err) {
      console.error("dismissFinding failed", err);
      const verb = dismissed
        ? scope === "epc_only"
          ? "hide from the EPC summary"
          : "dismiss"
        : scope === "epc_only"
          ? "show in the EPC summary again"
          : "reinstate";
      window.alert(
        `Couldn't ${verb} the finding: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  };

  if (isLoading || !data) {
    return <div className={styles.loadingState}>Loading procurement assessment…</div>;
  }

  // Every money value below this point renders through this currency
  // (£ for GBP / unlinked, € for EUR); provided once so every card,
  // the EPC summary and the xlsx export agree.
  const currency = makeCurrency(data.currency_code);

  if (data.status === "not_assessable") {
    return (
      <div className={styles.tab}>
        <Heading />
        <InputsPanel data={data} disabled={isRunning} />
        {isRunning && <ProgressIndicator stage={runningStage} />}
        {runError && <div className={styles.runError}>{runError}</div>}
        <div className={styles.emptyState}>
          <p className={styles.emptyTitle}>Not ready to assess</p>
          <p className={styles.emptyBody}>
            The assessment needs an EPC cost spreadsheet and an on-site survey
            it can read. Add or fix them on the Files tab, then run it here.
          </p>
          <InputChecklist inputs={data.inputs} />
        </div>
      </div>
    );
  }

  if (data.status === "not_run") {
    return (
      <CurrencyContext.Provider value={currency}>
        <div className={styles.tab}>
          <Heading />
          <InputsPanel data={data} disabled={isRunning} />
          <RunButton
            isRunning={isRunning}
            label="Run procurement assessment"
            runningLabel={runningStage}
            onClick={onRun}
          />
          {isRunning && <ProgressIndicator stage={runningStage} />}
          {runError && <div className={styles.runError}>{runError}</div>}
        </div>
      </CurrencyContext.Provider>
    );
  }

  // status === "fresh" | "stale"
  const inputsReady = procurementInputsReady(data.inputs);
  const allRows = [...data.epc_cost_rows, ...data.epc_extra_rows];
  const grouped = groupFindingsByRow(allRows, data.findings);
  return (
    <CurrencyContext.Provider value={currency}>
    <div className={styles.tab}>
      <Heading
        action={
          <div className={styles.headerActions}>
            {assessmentId && (
              <ViewAiTraceButton
                surface="procurement"
                artefactId={assessmentId}
                hasTrace={
                  data.status === "fresh" || data.status === "stale"
                    ? data.has_ai_trace
                    : false
                }
              />
            )}
            <SummarizeForEpcButton
              rows={allRows}
              grouped={grouped}
              projectName={projectName}
            />
            {(inputsReady || isRunning) && (
              <RunButton
                isRunning={isRunning}
                label={data.status === "stale" ? "Re-run procurement assessment" : "Re-run"}
                runningLabel={runningStage}
                onClick={onRun}
              />
            )}
          </div>
        }
      />
      <InputsPanel data={data} disabled={isRunning} />
      {isRunning && <ProgressIndicator stage={runningStage} />}
      {runError && <div className={styles.runError}>{runError}</div>}
      <AssessmentBanner data={data} />
      {data.findings.benchmarks_recomputed_from_persisted && (
        <CohortsRefreshedBanner />
      )}
      <PricingCard
        pricing={data.findings.pricing_assessment}
        explanation={data.findings.pricing_explanation}
        findings={data.findings}
      />
      <BenchmarksCard
        benchmarks={data.findings.benchmarks}
        currentAdjustedPerKwp={data.findings.pricing_assessment.adjusted_per_kwp}
      />
      <CostRowsTable
        rows={allRows}
        findings={grouped}
        onToggleDismiss={onToggleDismiss}
        dismissBusy={dismissFinding.isLoading}
      />
      <UnmatchedFindings
        findings={grouped.unmatched}
        onToggleDismiss={onToggleDismiss}
        dismissBusy={dismissFinding.isLoading}
      />
      <ProvenanceLine data={data} />
    </div>
    </CurrencyContext.Provider>
  );
};

// ---------- pieces ----------

const inputEntries = (inputs: ProcurementInputs) =>
  Object.entries(inputs) as [ProcurementInputKind, ProcurementInputState][];

const unreadyInputs = (inputs: ProcurementInputs): string[] =>
  inputEntries(inputs)
    .filter(([, state]) => state !== "ready")
    .map(([kind, state]) => describeProcurementInput(kind, state));

const InputChecklist = ({ inputs }: { inputs: ProcurementInputs }) => (
  <ul className={styles.emptyChecklist}>
    {inputEntries(inputs).map(([kind, state]) => (
      <li
        key={kind}
        className={`${styles.emptyCheck} ${
          state === "ready" ? styles.emptyCheckDone : styles.emptyCheckMissing
        }`}
      >
        {state === "ready" ? "✓" : "○"} {describeProcurementInput(kind, state)}
      </li>
    ))}
  </ul>
);

const AUTOMATIC = "";

const formatDay = (iso: string): string =>
  new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const OPTION_STATE_SUFFIX: Record<ProcurementInputState, string> = {
  ready: "",
  missing: "",
  extracting: " (still being read)",
  unreadable: " (can't be read)",
};

// Which file each input reads, and a picker to choose another.
const InputsPanel = ({
  data,
  disabled,
}: {
  data: ProcurementGetResult;
  disabled: boolean;
}) => {
  const choose = useChooseProcurementInput();
  const [error, setError] = useState<string | null>(null);
  const onChange = async (kind: ProcurementInputKind, value: string) => {
    setError(null);
    try {
      await choose.mutateAsync({
        project_id: data.project_id,
        kind,
        upload_id: value === AUTOMATIC ? null : value,
      });
    } catch (err) {
      setError(
        `Couldn't change the ${PROCUREMENT_INPUT_LABEL[kind].toLowerCase()}: ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
    }
  };
  // A tab opened before the deploy reads a payload without the choices.
  if (!data.input_choices) return null;
  return (
    <div className={styles.inputsPanel}>
      {(Object.keys(PROCUREMENT_INPUT_LABEL) as ProcurementInputKind[]).map((kind) => (
        <InputPicker
          key={kind}
          kind={kind}
          choice={data.input_choices[kind]}
          disabled={disabled || choose.isLoading}
          onChange={(value) => onChange(kind, value)}
        />
      ))}
      <p className={styles.inputNote}>
        Automatic uses the newest file the assessment can read, and for the cost
        spreadsheet prefers a workbook over a PDF or Word quote, and either over a CSV.
        It passes over a file that looks like another site's.
      </p>
      {error && <div className={styles.runError}>{error}</div>}
    </div>
  );
};

const InputPicker = ({
  kind,
  choice,
  disabled,
  onChange,
}: {
  kind: ProcurementInputKind;
  choice: ProcurementInputChoice;
  disabled: boolean;
  onChange: (value: string) => void;
}) => {
  const selected = choice.options.find((o) => o.upload_id === choice.selected_upload_id);
  const [viewing, setViewing] = useState(false);
  const newer = selected
    ? choice.options.filter((o) => o.created_at > selected.created_at).length
    : 0;
  const id = `procurement-input-${kind}`;
  return (
    <div className={styles.inputRow}>
      <label className={styles.inputLabel} htmlFor={id}>
        {PROCUREMENT_INPUT_LABEL[kind]}
      </label>
      <select
        id={id}
        className={styles.inputSelect}
        value={choice.chosen ? (choice.selected_upload_id ?? AUTOMATIC) : AUTOMATIC}
        disabled={disabled || choice.options.length === 0}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value={AUTOMATIC}>Automatic</option>
        {choice.options.map((o) => (
          <option key={o.upload_id} value={o.upload_id} disabled={o.state === "unreadable"}>
            {`${o.original_name} · ${formatDay(o.created_at)}${OPTION_STATE_SUFFIX[o.state]}${
              o.other_site ? ` (possibly ${o.other_site}'s)` : ""
            }`}
          </option>
        ))}
      </select>
      <div className={styles.inputDetail}>
        <span className={styles.inputNote}>
          {choice.chosen_unavailable ? "The chosen file is no longer available. " : ""}
          {selected
            ? `Using ${selected.original_name}, added ${formatDay(selected.created_at)}.`
            : ""}
          {choice.chosen && newer > 0
            ? ` ${newer} newer file${newer === 1 ? "" : "s"}.`
            : ""}
          {selected && (
            <button type="button" className={styles.inputView} onClick={() => setViewing(true)}>
              View file
            </button>
          )}
        </span>
        {selected && (
          <SightingList sightings={choice.selected_sightings ?? []} collapseSingle />
        )}
      </div>
      {viewing && selected && (
        <SourceTextModal
          uploadId={selected.upload_id}
          hasSourceText={selected.has_extracted_text ?? true}
          sourceLabel={selected.original_name}
          contentType={selected.content_type}
          onClose={() => setViewing(false)}
        />
      )}
    </div>
  );
};

const Heading = ({ action }: { action?: React.ReactNode }) => (
  <div className={styles.headerRow}>
    <div>
      <h2 className={styles.heading}>Procurement assessment</h2>
      <p className={styles.subheading}>
        Per-row read of the EPC cost vs the on-site survey. Each row carries
        its cost, cost per kWp, comment from the EPC, relevant survey context,
        and any follow-up question, risk, opportunity, or alignment flag.
      </p>
    </div>
    {action && <div>{action}</div>}
  </div>
);

const RunButton = ({
  isRunning,
  label,
  runningLabel,
  onClick,
}: {
  isRunning: boolean;
  label: string;
  runningLabel: string;
  onClick: () => void;
}) => (
  <div className={styles.runButtonWrapper}>
    <button
      type="button"
      className={styles.runButton}
      onClick={onClick}
      disabled={isRunning}
    >
      {isRunning ? runningLabel : label}
    </button>
  </div>
);

const ProgressIndicator = ({ stage }: { stage: string }) => (
  <div className={styles.progressIndicator}>
    <div className={styles.progressBar}>
      <div className={styles.progressFill} />
    </div>
    <div className={styles.progressLabel}>
      {stage} Don't refresh; this page will update on its own.
    </div>
  </div>
);

// Shown when the live benchmark rollup the BE recomputed at GET time
// differs structurally from the rollup persisted with the assessment
// (a new cohort qualified, percentiles shifted, etc.). The cohort
// table the operator is looking at IS live - this just flags that the
// LLM-written pricing_explanation was written against an older
// rollup, so the narrative might not reference the now-available
// cohort comparison. Operator can hit Re-run to refresh the
// narrative.
const CohortsRefreshedBanner = () => (
  <div className={styles.cohortsRefreshedBanner}>
    <strong>Cohort data has updated.</strong>
    <span>
      {" "}
      The Benchmarks card below is live, but the LLM pricing
      explanation was written before this cohort data was available.
      Re-run to refresh the narrative.
    </span>
  </div>
);

// One banner, so a stale assessment never says "Re-run" while Re-run is hidden.
const AssessmentBanner = ({ data }: { data: ProcurementAssessmentPayload }) => {
  const stale = data.status === "stale";
  const unready = unreadyInputs(data.inputs);
  if (!stale && unready.length === 0) return null;
  const reasonLabel = data.stale_reason
    ? (STALE_REASON_LABEL[data.stale_reason] ?? data.stale_reason)
    : "the source files changed";
  const rerun = unready.length === 0 ? "Re-run to refresh." : "It can't be re-run yet.";
  return (
    <div className={styles.staleBanner}>
      <strong>{stale ? "This assessment is stale." : "Can't re-run yet."}</strong>
      {stale && <span>{`Since this assessment was produced, ${reasonLabel}. ${rerun}`}</span>}
      {unready.length > 0 && <span>{`${unready.join(". ")}. See the Files tab.`}</span>}
    </div>
  );
};

// Split the LLM's pricing explanation into bullets. The prompt now
// asks for "- " prefixed lines; if the model produces a prose
// paragraph anyway we fall back to one bullet so the renderer still
// reads as a list.
const splitBullets = (raw: string): string[] => {
  const trimmed = raw.trim();
  if (!trimmed) return [];
  const lines = trimmed.split("\n").map((l) => l.trim()).filter(Boolean);
  const looksBulleted = lines.every((l) => /^[-*·]\s+/.test(l));
  if (looksBulleted) return lines.map((l) => l.replace(/^[-*·]\s+/, ""));
  return [trimmed];
};

// Aggregate cost risks (cost up) and opportunities (cost down) into
// best-case / worst-case totals + £/kWp. Pure FE math - the inputs
// are already on the findings the BE returns.
interface PricingBracket {
  worstTotal: number | null;
  worstPerKwp: number | null;
  bestTotal: number | null;
  bestPerKwp: number | null;
  riskSum: number;
  opportunitySum: number;
}

const computePricingBracket = (
  pricing: PricingAssessment,
  findings: ProcurementFindings,
): PricingBracket => {
  // Dismissed risks/opportunities are filtered out of the bracket
  // sums - the operator told us they don't believe the finding, so
  // its £-impact shouldn't drive the worst/best-case math either.
  const dismissals = findings.dismissals ?? {};
  const riskSum = findings.cost_risks.reduce(
    (acc, r, i) =>
      dismissals[`risk:${i}`]
        ? acc
        : acc + (r.estimated_impact_pounds ?? 0),
    0,
  );
  const opportunitySum = findings.cost_opportunities.reduce(
    (acc, o, i) =>
      dismissals[`opportunity:${i}`]
        ? acc
        : acc + (o.estimated_saving_pounds ?? 0),
    0,
  );
  const base = pricing.adjusted_total_pounds;
  const systemSize =
    base !== null && pricing.adjusted_per_kwp !== null && pricing.adjusted_per_kwp > 0
      ? base / pricing.adjusted_per_kwp
      : null;
  if (base === null) {
    return {
      worstTotal: null,
      worstPerKwp: null,
      bestTotal: null,
      bestPerKwp: null,
      riskSum,
      opportunitySum,
    };
  }
  const worstTotal = base + riskSum;
  const bestTotal = Math.max(0, base - opportunitySum);
  return {
    worstTotal,
    worstPerKwp: systemSize !== null ? worstTotal / systemSize : null,
    bestTotal,
    bestPerKwp: systemSize !== null ? bestTotal / systemSize : null,
    riskSum,
    opportunitySum,
  };
};

const PricingCard = ({
  pricing,
  explanation,
  findings,
}: {
  pricing: PricingAssessment;
  explanation: string;
  findings: ProcurementFindings;
}) => {
  const cur = useCurrency();
  const adjusted = cur.perKwp(pricing.adjusted_per_kwp);
  const target = pricing.target_per_kwp;
  const bullets = splitBullets(explanation);
  const bracket = computePricingBracket(pricing, findings);
  return (
    <div className={styles.pricingCard}>
      <div className={styles.pricingRow}>
        <span className={`${styles.ragBadge} ${RAG_CLASS[pricing.rag]}`}>
          {RAG_LABEL[pricing.rag]}
        </span>
        <div className={styles.pricingFigures}>
          <span className={styles.pricingHeadline}>{adjusted}</span>
          <span className={styles.pricingSub}>
            Adjusted {cur.symbol}/kWp
            {target !== null && (
              <>
                {" "}
                · target {cur.symbol}
                {Math.round(target).toLocaleString("en-GB")} / kWp
              </>
            )}
          </span>
        </div>
      </div>
      {pricing.adjusted_total_pounds !== null && (
        <div className={styles.exclusionBridge}>
          Raw EPC total <strong>{cur.money(pricing.raw_total_pounds)}</strong>
          {pricing.excluded_lines.length > 0 ? (
            <>
              {" minus free-issued lines ("}
              {pricing.excluded_lines.map((line, i) => (
                <span key={i}>
                  {i > 0 && ", "}
                  <strong>{line.label}</strong> {cur.money(line.total_cost)}
                </span>
              ))}
              {") = adjusted "}
            </>
          ) : (
            <> · no free-issued lines identified · adjusted </>
          )}
          <strong>{cur.money(pricing.adjusted_total_pounds)}</strong>.
        </div>
      )}
      {bullets.length > 0 && (
        <DemoBlur>
          <ul className={styles.pricingBullets}>
            {bullets.map((b, i) => (
              <li key={i}>{b}</li>
            ))}
          </ul>
        </DemoBlur>
      )}
      {(bracket.worstTotal !== null || bracket.bestTotal !== null) && (
        <div className={styles.bracketRow}>
          {bracket.bestTotal !== null && bracket.opportunitySum > 0 && (
            <div className={`${styles.bracketTile} ${styles.bracketBest}`}>
              <span className={styles.bracketLabel}>Best case</span>
              <span className={styles.bracketHeadline}>
                {cur.perKwp(bracket.bestPerKwp)}
              </span>
              <span className={styles.bracketSub}>
                {cur.money(bracket.bestTotal)} · all opportunities captured
                (− {cur.money(bracket.opportunitySum)})
              </span>
            </div>
          )}
          {bracket.worstTotal !== null && bracket.riskSum > 0 && (
            <div className={`${styles.bracketTile} ${styles.bracketWorst}`}>
              <span className={styles.bracketLabel}>Worst case</span>
              <span className={styles.bracketHeadline}>
                {cur.perKwp(bracket.worstPerKwp)}
              </span>
              <span className={styles.bracketSub}>
                {cur.money(bracket.worstTotal)} · all risks materialise
                (+ {cur.money(bracket.riskSum)})
              </span>
            </div>
          )}
        </div>
      )}
      {pricing.notes.length > 0 && (
        <ul className={styles.pricingNotes}>
          {pricing.notes.map((note, i) => (
            <li key={i}>· {note}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

// Cohorts surface here in priority order: most-specific (EPC) at the top,
// global at the bottom. Lets the operator scan from "what we've seen this
// EPC charge" to "what every comparable project we've ever assessed
// looked like".
const COHORT_KIND_LABEL: Record<BenchmarkCohort["cohort_kind"], string> = {
  epc: "Same EPC",
  tenant_landlord: "Same tenant + landlord",
  size_band: "Same size band",
  global: "All comparable projects",
};

// Operator-facing names for the BE's machine cohort_kinds in the
// "Not yet compared" footer.
const SKIPPED_COHORT_LABEL: Record<string, string> = {
  epc: "Same EPC",
  tenant_landlord: "Same tenant + landlord",
  size_band: "Same size band",
  global: "All comparable projects",
  all: "All cohorts",
};

const positionAgainstCohort = (
  current: number | null,
  cohort: BenchmarkCohort,
): { tone: "above" | "in" | "below"; text: string } => {
  if (current === null || !Number.isFinite(current)) {
    return { tone: "in", text: "Current per-kWp figure not available" };
  }
  if (current > cohort.p75) {
    return { tone: "above", text: "Above p75 - this quote is high for the cohort" };
  }
  if (current < cohort.p25) {
    return { tone: "below", text: "Below p25 - this quote is low for the cohort" };
  }
  return { tone: "in", text: "Inside the p25-p75 band - in line with the cohort" };
};

const BenchmarksCard = ({
  benchmarks,
  currentAdjustedPerKwp,
}: {
  benchmarks: BenchmarkRollup | null;
  currentAdjustedPerKwp: number | null;
}) => {
  const cur = useCurrency();
  // No rollup at all - cold-start env, or the procurement worker
  // didn't compute one. Hide the card entirely rather than render
  // an empty box (the pricing card already carries the target
  // comparison for this case).
  if (!benchmarks) {
    return null;
  }
  const hasAny = benchmarks.cohorts.length > 0;
  const hasSkipped = benchmarks.skipped.length > 0;
  if (!hasAny && !hasSkipped) {
    return null;
  }
  return (
    <div className={styles.benchmarksCard}>
      <div className={styles.benchmarksHeader}>
        <h3 className={styles.benchmarksTitle}>Benchmarked against past projects</h3>
        {benchmarks.currency_code && (
          <span className={styles.benchmarksCurrency}>
            {benchmarks.currency_code} cohort
          </span>
        )}
      </div>
      {hasAny ? (
        <table className={styles.benchmarksTable}>
          <thead>
            <tr>
              <th>Cohort</th>
              <th className={styles.benchmarksNumeric}>N</th>
              <th className={styles.benchmarksNumeric}>p25</th>
              <th className={styles.benchmarksNumeric}>p50</th>
              <th className={styles.benchmarksNumeric}>p75</th>
              <th className={styles.benchmarksNumeric}>This site</th>
              <th>This project</th>
            </tr>
          </thead>
          <tbody>
            {benchmarks.cohorts.map((cohort) => {
              const position = positionAgainstCohort(currentAdjustedPerKwp, cohort);
              return (
                <tr key={`${cohort.cohort_kind}-${cohort.discriminator ?? ""}`}>
                  <td>
                    <div className={styles.benchmarksCohortLabel}>
                      {COHORT_KIND_LABEL[cohort.cohort_kind]}
                    </div>
                    {cohort.discriminator && (
                      <div className={styles.benchmarksDiscriminator}>
                        {cohort.discriminator}
                      </div>
                    )}
                    {cohort.members && cohort.members.length > 0 && (
                      <div className={styles.benchmarksMembers}>
                        {cohort.members.map((m, idx) => (
                          <span key={m.project_id}>
                            <Link
                              to={`/project/${encodeURIComponent(m.project_id)}/procurement`}
                              className={styles.benchmarksMemberLink}
                            >
                              {m.name ?? m.project_id}
                            </Link>
                            {idx < (cohort.members?.length ?? 0) - 1 ? ", " : ""}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className={styles.benchmarksNumeric}>{cohort.n}</td>
                  <td className={styles.benchmarksNumeric}>
                    {cur.perKwpShort(cohort.p25)}
                  </td>
                  <td className={`${styles.benchmarksNumeric} ${styles.benchmarksMedian}`}>
                    {cur.perKwpShort(cohort.p50)}
                  </td>
                  <td className={styles.benchmarksNumeric}>
                    {cur.perKwpShort(cohort.p75)}
                  </td>
                  <td className={`${styles.benchmarksNumeric} ${styles.benchmarksThisSite}`}>
                    {cur.perKwpShort(currentAdjustedPerKwp)}
                  </td>
                  <td>
                    <span
                      className={`${styles.benchmarksPosition} ${
                        position.tone === "above"
                          ? styles.benchmarksPositionAbove
                          : position.tone === "below"
                            ? styles.benchmarksPositionBelow
                            : styles.benchmarksPositionIn
                      }`}
                    >
                      {position.text}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <p className={styles.benchmarksEmpty}>
          No cohort had enough comparable projects to populate a benchmark yet.
        </p>
      )}
      {hasSkipped && (
        <div className={styles.benchmarksSkipped}>
          <span className={styles.benchmarksSkippedLabel}>Not yet compared:</span>{" "}
          <ul className={styles.benchmarksSkippedList}>
            {benchmarks.skipped.map((skip, i) => (
              <li key={`${skip.cohort_kind}-${i}`}>
                <strong>{SKIPPED_COHORT_LABEL[skip.cohort_kind] ?? skip.cohort_kind}</strong>{" "}
                — {skip.reason}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

type ToggleDismiss = (
  kind: FindingKind,
  index: number,
  dismissed: boolean,
  scope?: "dismiss" | "epc_only",
) => void | Promise<void>;

const CostRowsTable = ({
  rows,
  findings,
  onToggleDismiss,
  dismissBusy,
}: {
  rows: EpcCostRow[];
  findings: { byRow: Map<string, PerRowFindings>; unmatched: PerRowFindings } | null;
  onToggleDismiss?: ToggleDismiss;
  dismissBusy?: boolean;
}) => {
  type RowFilter = "all" | "followups" | "risks" | "opportunities";
  const [filter, setFilter] = useState<RowFilter>("all");

  // Totals across all rows (rather than just the visible / filtered
  // ones) drive the chip counts so operators always see the full
  // landscape; the chip values don't shift as filters change.
  const totals = useMemo(() => {
    let followups = 0;
    let risks = 0;
    let opportunities = 0;
    if (findings) {
      for (const r of findings.byRow.values()) {
        followups += r.followups.length;
        risks += r.risks.length;
        opportunities += r.opportunities.length;
      }
    }
    return { followups, risks, opportunities };
  }, [findings]);

  // The "EPC cost rows N" chip itself is a no-op visually-active
  // when filter==="all"; clicking another chip flips off "all".
  const rowMatchesFilter = (rowLabel: string): boolean => {
    if (filter === "all") return true;
    const f = findings?.byRow.get(rowLabel);
    if (!f) return false;
    if (filter === "followups") return f.followups.length > 0;
    if (filter === "risks") return f.risks.length > 0;
    if (filter === "opportunities") return f.opportunities.length > 0;
    return true;
  };

  if (rows.length === 0) {
    return (
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>EPC cost rows</h3>
        <p className={styles.emptyListNote}>No cost rows extracted yet.</p>
      </div>
    );
  }

  const visibleRows = rows.filter((row) => rowMatchesFilter(row.label));
  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <button
          type="button"
          className={`${styles.filterChip} ${filter === "all" ? styles.filterChipActive : ""}`}
          onClick={() => setFilter("all")}
        >
          <span className={styles.filterChipLabel}>EPC cost rows</span>
          <span className={styles.filterChipCount}>{rows.length}</span>
        </button>
        <button
          type="button"
          className={`${styles.filterChip} ${styles.filterChipFollowup} ${
            filter === "followups" ? styles.filterChipActive : ""
          }`}
          onClick={() => setFilter("followups")}
          disabled={totals.followups === 0}
        >
          <span className={styles.filterChipLabel}>Follow-ups</span>
          <span className={styles.filterChipCount}>{totals.followups}</span>
        </button>
        <button
          type="button"
          className={`${styles.filterChip} ${styles.filterChipRisk} ${
            filter === "risks" ? styles.filterChipActive : ""
          }`}
          onClick={() => setFilter("risks")}
          disabled={totals.risks === 0}
        >
          <span className={styles.filterChipLabel}>Cost risks</span>
          <span className={styles.filterChipCount}>{totals.risks}</span>
        </button>
        <button
          type="button"
          className={`${styles.filterChip} ${styles.filterChipOpportunity} ${
            filter === "opportunities" ? styles.filterChipActive : ""
          }`}
          onClick={() => setFilter("opportunities")}
          disabled={totals.opportunities === 0}
        >
          <span className={styles.filterChipLabel}>Cost opportunities</span>
          <span className={styles.filterChipCount}>{totals.opportunities}</span>
        </button>
      </div>
      <div className={styles.rowsTable}>
        {visibleRows.length === 0 ? (
          <p className={styles.emptyListNote}>
            No cost rows match this filter. Click <strong>EPC cost rows</strong>{" "}
            to clear.
          </p>
        ) : (
          visibleRows.map((row, i) => (
            <CostRowCard
              key={`${row.label}-${i}`}
              row={row}
              perRow={findings?.byRow.get(row.label) ?? null}
              onToggleDismiss={onToggleDismiss}
              dismissBusy={dismissBusy}
            />
          ))
        )}
      </div>
    </div>
  );
};

const CostRowCard = ({
  row,
  perRow,
  onToggleDismiss,
  dismissBusy,
}: {
  row: EpcCostRow;
  perRow: PerRowFindings | null;
  onToggleDismiss?: ToggleDismiss;
  dismissBusy?: boolean;
}) => {
  const cur = useCurrency();
  const hasAnyFindings =
    perRow &&
    (perRow.followups.length > 0 ||
      perRow.risks.length > 0 ||
      perRow.opportunities.length > 0 ||
      perRow.alignments.length > 0);

  // Pull survey context out of the alignments for this row. The
  // alignment rationales already paraphrase the surveyor's findings in
  // natural language; we deliberately do NOT surface
  // ``cited_survey_paths`` (e.g. ``survey.electrical.intake.notes``)
  // anywhere on the operator card - they're internal audit-trail
  // references, not human-readable copy.
  const surveyContext: string[] = [];
  if (perRow) {
    for (const a of perRow.alignments) {
      if (a.rationale) surveyContext.push(a.rationale);
    }
  }

  return (
    <div className={styles.rowCard}>
      {/* Single line, left-justified: label · cost · £/kWp · EPC
       * comment. Mirrors the spreadsheet row shape so the operator
       * can scan the table at a glance instead of chasing values
       * to the right edge.
       */}
      <div className={styles.rowHeader}>
        <span className={styles.rowLabel}>{row.label}</span>
        <span className={styles.rowMetric}>{cur.money(row.total_cost)}</span>
        <span className={styles.rowMetricMuted}>
          {row.per_kwp !== null ? `${cur.symbol}${row.per_kwp.toFixed(2)}/kWp` : "— /kWp"}
        </span>
        {row.comment && (
          <span className={styles.rowComment}>· {row.comment}</span>
        )}
      </div>
      <div className={styles.rowBody}>
        <RowField
          label="Survey context"
          value={surveyContext.length > 0 ? surveyContext : null}
        />
        {perRow && perRow.followups.length > 0 && (
          <RowFindingBlock label="EPC follow-up">
            {perRow.followups.map((f, i) => (
              <RowFindingLine
                key={i}
                priority={f.priority}
                primary={f.question}
                secondary={f.why_asked}
                dismissal={f._dismissal}
                epcHidden={f._epcHidden}
                onToggleDismiss={
                  onToggleDismiss
                    ? (dismiss) =>
                        onToggleDismiss(f._kind, f._index, dismiss)
                    : undefined
                }
                onToggleEpcHidden={
                  onToggleDismiss
                    ? (hide) =>
                        onToggleDismiss(f._kind, f._index, hide, "epc_only")
                    : undefined
                }
                dismissBusy={dismissBusy}
              />
            ))}
          </RowFindingBlock>
        )}
        {perRow && perRow.risks.length > 0 && (
          <RowFindingBlock label="Cost risk">
            {perRow.risks.map((r, i) => (
              <RowFindingLine
                key={i}
                primary={r.area}
                secondary={r.scenario}
                impact={
                  r.estimated_impact_pounds !== null
                    ? `+ ${cur.money(r.estimated_impact_pounds)}`
                    : null
                }
                dismissal={r._dismissal}
                epcHidden={r._epcHidden}
                onToggleDismiss={
                  onToggleDismiss
                    ? (dismiss) =>
                        onToggleDismiss(r._kind, r._index, dismiss)
                    : undefined
                }
                onToggleEpcHidden={
                  onToggleDismiss
                    ? (hide) =>
                        onToggleDismiss(r._kind, r._index, hide, "epc_only")
                    : undefined
                }
                dismissBusy={dismissBusy}
              />
            ))}
          </RowFindingBlock>
        )}
        {perRow && perRow.opportunities.length > 0 && (
          <RowFindingBlock label="Cost opportunity">
            {perRow.opportunities.map((o, i) => (
              <RowFindingLine
                key={i}
                primary={o.area}
                secondary={o.how}
                impact={
                  o.estimated_saving_pounds !== null
                    ? `- ${cur.money(o.estimated_saving_pounds)}`
                    : null
                }
                dismissal={o._dismissal}
                epcHidden={o._epcHidden}
                onToggleDismiss={
                  onToggleDismiss
                    ? (dismiss) =>
                        onToggleDismiss(o._kind, o._index, dismiss)
                    : undefined
                }
                onToggleEpcHidden={
                  onToggleDismiss
                    ? (hide) =>
                        onToggleDismiss(o._kind, o._index, hide, "epc_only")
                    : undefined
                }
                dismissBusy={dismissBusy}
              />
            ))}
          </RowFindingBlock>
        )}
        {perRow && perRow.alignments.length > 0 && (
          <RowFindingBlock label="Cost vs survey">
            {perRow.alignments.map((a, i) => (
              <RowFindingLine
                key={i}
                status={a.status}
                primary={
                  a.status === "aligned"
                    ? "Aligned"
                    : a.status === "over_costed"
                      ? "Cost looks high"
                      : a.status === "under_costed_or_missing"
                        ? "Cost looks low or missing"
                        : "Informational"
                }
                secondary={a.rationale}
                dismissal={a._dismissal}
                epcHidden={a._epcHidden}
                onToggleDismiss={
                  onToggleDismiss
                    ? (dismiss) =>
                        onToggleDismiss(a._kind, a._index, dismiss)
                    : undefined
                }
                onToggleEpcHidden={
                  onToggleDismiss
                    ? (hide) =>
                        onToggleDismiss(a._kind, a._index, hide, "epc_only")
                    : undefined
                }
                dismissBusy={dismissBusy}
              />
            ))}
          </RowFindingBlock>
        )}
        {!hasAnyFindings && perRow !== null && (
          <div className={styles.rowQuiet}>No procurement findings on this row.</div>
        )}
      </div>
    </div>
  );
};

const RowField = ({
  label,
  value,
}: {
  label: string;
  value: string | string[] | null;
}) => {
  if (value === null || (Array.isArray(value) && value.length === 0)) {
    return (
      <div className={styles.rowField}>
        <span className={styles.rowFieldLabel}>{label}</span>
        <span className={styles.rowFieldMuted}>—</span>
      </div>
    );
  }
  if (Array.isArray(value)) {
    return (
      <div className={styles.rowField}>
        <span className={styles.rowFieldLabel}>{label}</span>
        <ul className={styles.rowFieldList}>
          {value.map((v, i) => (
            <li key={i}>{v}</li>
          ))}
        </ul>
      </div>
    );
  }
  return (
    <div className={styles.rowField}>
      <span className={styles.rowFieldLabel}>{label}</span>
      <span className={styles.rowFieldValue}>{value}</span>
    </div>
  );
};

const RowFindingBlock = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className={styles.rowFindingBlock}>
    <span className={styles.rowFieldLabel}>{label}</span>
    <ul className={styles.rowFindingList}>{children}</ul>
  </div>
);

const formatDismissedAt = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString("en-GB");
};

const RowFindingLine = ({
  primary,
  secondary,
  impact,
  priority,
  status,
  dismissal,
  epcHidden,
  onToggleDismiss,
  onToggleEpcHidden,
  dismissBusy,
}: {
  primary: string;
  secondary?: string;
  impact?: string | null;
  priority?: "high" | "medium" | "low";
  status?: string;
  dismissal?: { by: string; at: string } | null;
  epcHidden?: { by: string; at: string } | null;
  onToggleDismiss?: (dismiss: boolean) => void | Promise<void>;
  onToggleEpcHidden?: (hide: boolean) => void | Promise<void>;
  dismissBusy?: boolean;
}) => {
  const isDismissed = Boolean(dismissal);
  const isEpcHidden = Boolean(epcHidden);
  // ``by="default"`` is the system-set default for risks +
  // opportunities (Noaa, 2026-06-09: those kinds are hidden from the
  // EPC unless the operator explicitly clicks Show to EPC). Treat
  // these as silently-defaulted: no attribution line, no extra
  // chrome - the row just doesn't show in the EPC text/xlsx, and the
  // operator only sees "Show to EPC" as an affordance to surface it.
  const isSystemDefaultHidden =
    isEpcHidden && epcHidden && epcHidden.by === "default";
  return (
    <li
      className={`${styles.rowFindingItem} ${
        isDismissed ? styles.rowFindingItemDismissed : ""
      }`}
    >
      <div className={styles.rowFindingHeader}>
        <span className={styles.rowFindingPrimary}>{primary}</span>
        {/* Impact (e.g. "+ £8,000") sits inline next to the title -
            left-justified with the rest of the left-group, not floated
            right (Noaa, 2026-06-09). The action buttons cluster on the
            right via the .rowFindingActions wrapper below. */}
        {impact && !isDismissed && (
          <span className={styles.rowFindingImpact}>{impact}</span>
        )}
        {priority && !isDismissed && (
          <span className={`${styles.tag} ${priorityClass(priority)}`}>
            {priority}
          </span>
        )}
        {status && !isDismissed && (
          <span className={`${styles.tag} ${statusClass(status)}`}>{status}</span>
        )}
        <div className={styles.rowFindingActions}>
          {/* Hide from EPC: softer than Dismiss - filters from the EPC
              text + xlsx only, keeps the row on the platform. Only
              shown when not dismissed (Dismiss is the strict superset)
              and not already hidden. */}
          {onToggleEpcHidden && !isDismissed && !isEpcHidden && (
            <button
              type="button"
              className={styles.rowFindingDismiss}
              onClick={() => onToggleEpcHidden(true)}
              disabled={dismissBusy}
              title="Hide this finding from the EPC summary text + xlsx. The finding stays visible on the platform assessment and still counts in the worst/best-case bracket."
            >
              Hide from EPC
            </button>
          )}
          {onToggleEpcHidden && !isDismissed && isEpcHidden && (
            <button
              type="button"
              className={styles.rowFindingReinstate}
              onClick={() => onToggleEpcHidden(false)}
              disabled={dismissBusy}
              title="Show this finding in the EPC summary text + xlsx"
            >
              Show to EPC
            </button>
          )}
          {onToggleDismiss && !isDismissed && (
            <button
              type="button"
              className={styles.rowFindingDismiss}
              onClick={() => onToggleDismiss(true)}
              disabled={dismissBusy}
              title="Dismiss this finding entirely - removes it from the platform list, the EPC summary + xlsx, and the worst/best-case bracket."
            >
              Dismiss
            </button>
          )}
          {onToggleDismiss && isDismissed && (
            <button
              type="button"
              className={styles.rowFindingReinstate}
              onClick={() => onToggleDismiss(false)}
              disabled={dismissBusy}
              title="Bring this finding back into the platform list, the EPC summary + xlsx, and the worst/best-case bracket."
            >
              Reinstate
            </button>
          )}
        </div>
      </div>
      {secondary && (
        <DemoBlur>
          <div className={styles.rowFindingSecondary}>{secondary}</div>
        </DemoBlur>
      )}
      {isDismissed && dismissal && (
        <div className={styles.rowFindingDismissedNote}>
          Dismissed by {dismissal.by} on {formatDismissedAt(dismissal.at)}
        </div>
      )}
      {!isDismissed && isEpcHidden && epcHidden && !isSystemDefaultHidden && (
        <div className={styles.rowFindingDismissedNote}>
          Hidden from EPC by {epcHidden.by} on {formatDismissedAt(epcHidden.at)}
        </div>
      )}
    </li>
  );
};

const UnmatchedFindings = ({
  findings,
  onToggleDismiss,
  dismissBusy,
}: {
  findings: PerRowFindings;
  onToggleDismiss?: ToggleDismiss;
  dismissBusy?: boolean;
}) => {
  const cur = useCurrency();
  const total =
    findings.followups.length +
    findings.risks.length +
    findings.opportunities.length +
    findings.alignments.length;
  if (total === 0) return null;
  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>
        Findings not tied to a specific cost row
        <span className={styles.sectionCount}>{total}</span>
      </h3>
      <div className={styles.rowsTable}>
        <div className={styles.rowCard}>
          <div className={styles.rowBody}>
            {findings.followups.length > 0 && (
              <RowFindingBlock label="EPC follow-up">
                {findings.followups.map((f, i) => (
                  <RowFindingLine
                    key={i}
                    priority={f.priority}
                    primary={f.question}
                    secondary={f.why_asked}
                    dismissal={f._dismissal}
                    onToggleDismiss={
                      onToggleDismiss
                        ? (dismiss) =>
                            onToggleDismiss(f._kind, f._index, dismiss)
                        : undefined
                    }
                    epcHidden={f._epcHidden}
                    onToggleEpcHidden={
                      onToggleDismiss
                        ? (hide) =>
                            onToggleDismiss(f._kind, f._index, hide, "epc_only")
                        : undefined
                    }
                    dismissBusy={dismissBusy}
                  />
                ))}
              </RowFindingBlock>
            )}
            {findings.risks.length > 0 && (
              <RowFindingBlock label="Cost risk">
                {findings.risks.map((r, i) => (
                  <RowFindingLine
                    key={i}
                    primary={r.area}
                    secondary={r.scenario}
                    impact={
                      r.estimated_impact_pounds !== null
                        ? `+ ${cur.money(r.estimated_impact_pounds)}`
                        : null
                    }
                    dismissal={r._dismissal}
                    onToggleDismiss={
                      onToggleDismiss
                        ? (dismiss) =>
                            onToggleDismiss(r._kind, r._index, dismiss)
                        : undefined
                    }
                    epcHidden={r._epcHidden}
                    onToggleEpcHidden={
                      onToggleDismiss
                        ? (hide) =>
                            onToggleDismiss(r._kind, r._index, hide, "epc_only")
                        : undefined
                    }
                    dismissBusy={dismissBusy}
                  />
                ))}
              </RowFindingBlock>
            )}
            {findings.opportunities.length > 0 && (
              <RowFindingBlock label="Cost opportunity">
                {findings.opportunities.map((o, i) => (
                  <RowFindingLine
                    key={i}
                    primary={o.area}
                    secondary={o.how}
                    impact={
                      o.estimated_saving_pounds !== null
                        ? `- ${cur.money(o.estimated_saving_pounds)}`
                        : null
                    }
                    dismissal={o._dismissal}
                    onToggleDismiss={
                      onToggleDismiss
                        ? (dismiss) =>
                            onToggleDismiss(o._kind, o._index, dismiss)
                        : undefined
                    }
                    epcHidden={o._epcHidden}
                    onToggleEpcHidden={
                      onToggleDismiss
                        ? (hide) =>
                            onToggleDismiss(o._kind, o._index, hide, "epc_only")
                        : undefined
                    }
                    dismissBusy={dismissBusy}
                  />
                ))}
              </RowFindingBlock>
            )}
            {findings.alignments.length > 0 && (
              <RowFindingBlock label="Cost vs survey">
                {findings.alignments.map((a, i) => (
                  <RowFindingLine
                    key={i}
                    status={a.status}
                    primary={a.label}
                    secondary={a.rationale}
                    dismissal={a._dismissal}
                    onToggleDismiss={
                      onToggleDismiss
                        ? (dismiss) =>
                            onToggleDismiss(a._kind, a._index, dismiss)
                        : undefined
                    }
                    epcHidden={a._epcHidden}
                    onToggleEpcHidden={
                      onToggleDismiss
                        ? (hide) =>
                            onToggleDismiss(a._kind, a._index, hide, "epc_only")
                        : undefined
                    }
                    dismissBusy={dismissBusy}
                  />
                ))}
              </RowFindingBlock>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const priorityClass = (p: "high" | "medium" | "low") => {
  if (p === "high") return styles.tagPriorityHigh;
  if (p === "medium") return styles.tagPriorityMedium;
  return styles.tagPriorityLow;
};

const statusClass = (s: string) => {
  if (s === "aligned") return styles.tagAligned;
  if (s === "over_costed") return styles.tagOverCosted;
  if (s === "under_costed_or_missing") return styles.tagUnderCosted;
  return styles.tagInformational;
};

const ProvenanceLine = ({ data }: { data: ProcurementAssessmentPayload }) => {
  const at = new Date(data.created_at).toLocaleString("en-GB");
  const usage =
    data.usage.input_tokens !== null && data.usage.output_tokens !== null
      ? `${data.usage.input_tokens.toLocaleString("en-GB")} input + ${data.usage.output_tokens.toLocaleString("en-GB")} output tokens`
      : "";
  return (
    <div className={styles.metaLine}>
      Produced {at}
      {data.model_id ? ` by ${data.model_id}` : ""}
      {usage ? ` · ${usage}` : ""}
    </div>
  );
};

// ---------- Summarize-for-EPC ----------

// Compose plain-text per-row summaries the operator can paste into an
// email to the EPC. Written in InRange's voice addressing the EPC
// directly ("we'd like to confirm", "we noticed", "could you clarify")
// rather than as internal notes ("Confirm with EPC").
//
// Deliberately drops the LLM's ``cited_survey_paths`` (e.g.
// ``survey.electrical.intake.notes``) - those are JSON-path
// internals, not something an EPC reader can parse. The follow-up
// questions, risks and opportunities are emitted with the model's
// natural-language reasoning only.
const composeEpcSummary = (
  rows: EpcCostRow[],
  grouped: ReturnType<typeof groupFindingsByRow>,
  cur: Currency,
): string => {
  const blocks: string[] = [];
  for (const row of rows) {
    const f = grouped.byRow.get(row.label);
    if (!f) continue;
    // Dismissed and EPC-hidden findings both drop out of the summary -
    // the on-screen EPC text mirrors the xlsx that gets pasted into
    // the email, and `Hide from EPC` is the softer toggle (keeps the
    // finding on the platform assessment but pulls it from EPC text).
    const live = (
      <T extends DismissalMeta>(items: T[]): T[] =>
        items.filter((i) => i._dismissal === null && i._epcHidden === null)
    );
    const followups = live(f.followups);
    const risks = live(f.risks);
    const opportunities = live(f.opportunities);
    const nonAlignedAlignments = live(f.alignments).filter(
      (a) => a.status !== "aligned" && a.status !== "informational",
    );
    const hasAnything =
      followups.length +
        risks.length +
        opportunities.length +
        nonAlignedAlignments.length >
      0;
    if (!hasAnything) continue;

    const lines: string[] = [];
    const cost = row.total_cost !== null ? cur.money(row.total_cost) : "—";
    const perKwp = row.per_kwp !== null ? `${cur.symbol}${row.per_kwp.toFixed(2)}/kWp` : "—";
    lines.push(`${row.label} (${cost}, ${perKwp})`);
    if (row.comment) lines.push(`  Your comment on this line: ${row.comment}`);

    if (nonAlignedAlignments.length > 0) {
      lines.push("  How this lines up with the on-site survey:");
      for (const a of nonAlignedAlignments) {
        const tag =
          a.status === "over_costed"
            ? "The price looks high relative to the survey scope"
            : a.status === "under_costed_or_missing"
              ? "The price looks low (or missing) for what the survey found"
              : a.status;
        lines.push(`    - ${tag}: ${a.rationale}`);
      }
    }

    if (followups.length > 0) {
      lines.push("  We'd like to confirm the following:");
      for (const q of followups) {
        lines.push(`    - ${q.question}`);
        if (q.why_asked) lines.push(`      Why this matters to us: ${q.why_asked}`);
      }
    }

    if (risks.length > 0) {
      lines.push("  Risks we'd like to flag with you:");
      for (const r of risks) {
        const impact =
          r.estimated_impact_pounds !== null
            ? ` (we estimate this could add ~${cur.money(r.estimated_impact_pounds)})`
            : "";
        lines.push(`    - ${r.area}${impact}: ${r.scenario}`);
      }
    }

    if (opportunities.length > 0) {
      lines.push("  Is there room to reduce the price?");
      for (const o of opportunities) {
        const saving =
          o.estimated_saving_pounds !== null
            ? ` (we estimate this could save ~${cur.money(o.estimated_saving_pounds)})`
            : "";
        lines.push(`    - ${o.area}${saving}: ${o.how}`);
      }
    }

    blocks.push(lines.join("\n"));
  }
  if (blocks.length === 0) {
    return "We didn't find any follow-up items on this assessment.";
  }
  return blocks.join("\n\n");
};

// Section display names for the per-section header rows. The BE
// section ids are stable machine strings; these are what the EPC sees.
const SECTION_DISPLAY: Record<string, string> = {
  pre_construction: "Pre Construction",
  materials: "Materials",
  labour: "Labour",
  plant_welfare_access: "Plant / Welfare / Access",
  hs_site_management: "H&S / Site Management",
  testing_commissioning: "Testing / Commissioning",
  other: "Other",
};

const SECTION_ORDER = [
  "pre_construction",
  "materials",
  "labour",
  "plant_welfare_access",
  "hs_site_management",
  "testing_commissioning",
  "other",
];

// Build the per-site xlsx workbook in the same layered layout as the
// source EPC cost sheet - section headers, per-section subtotals, a
// final TOTAL row - with the Inrange commentary added as the
// rightmost column. Mirrors the Cowork "4 sites side by side"
// reference workbook Noaa flagged.
const buildEpcXlsx = async (
  rows: EpcCostRow[],
  grouped: ReturnType<typeof groupFindingsByRow>,
  projectName: string,
  cur: Currency,
): Promise<void> => {
  // The cost columns render in the project's currency (£ / €). Excel
  // number formats take the symbol as a literal prefix.
  const moneyFmt0 = `${cur.symbol}#,##0`;
  const moneyFmt2 = `${cur.symbol}#,##0.00`;
  const XLSXStyle = await loadXlsxStyle();

  // 6-column layout that mirrors the source EPC cost sheet (the
  // Cowork reference workbook):
  //   A: section header / "Total" / "TOTAL" markers
  //   B: line item label
  //   C: Cost total (£)
  //   D: £/kWp
  //   E: Your comment on this line (the EPC's original comment)
  //   F: Inrange commentary (our SURVEY / CONFIRM / RISK /
  //      OPPORTUNITY blocks)
  const HEADER_ROW = [
    "",
    "Item",
    `Cost total (${cur.symbol})`,
    `${cur.symbol}/kWp`,
    "Your comment on this line",
    "Inrange commentary",
  ];

  const colWidths = [
    { wch: 22 }, // section / Total / TOTAL
    { wch: 36 }, // Item
    { wch: 14 }, // Cost total
    { wch: 10 }, // £/kWp
    { wch: 40 }, // Your comment
    { wch: 90 }, // Inrange commentary
  ];

  const buildCommentary = (f: PerRowFindings | undefined): string => {
    if (!f) return "";
    const blocks: string[] = [];

    // Drop dismissed findings AND EPC-hidden findings - the EPC sees
    // only what the operator still believes is worth raising. ``Hide
    // from EPC`` is a softer toggle than ``Dismiss``: it filters the
    // finding out of the EPC text + xlsx while keeping it visible in
    // the platform assessment (Noaa's distinction, 2026-06-09).
    const live = <T extends DismissalMeta>(items: T[]): T[] =>
      items.filter((i) => i._dismissal === null && i._epcHidden === null);
    const nonAligned = live(f.alignments).filter(
      (a) => a.status !== "aligned" && a.status !== "informational",
    );
    for (const a of nonAligned) {
      const tag =
        a.status === "over_costed"
          ? "The price looks high for what the survey shows"
          : a.status === "under_costed_or_missing"
            ? "The price looks low (or missing) for what the survey found"
            : a.status;
      blocks.push(`${tag}.\n${a.rationale}`);
    }

    for (const q of live(f.followups)) {
      const lines = [q.question];
      if (q.why_asked) lines.push(q.why_asked);
      blocks.push(lines.join("\n"));
    }

    for (const r of live(f.risks)) {
      const impact =
        r.estimated_impact_pounds !== null
          ? ` (we estimate this could add ~${cur.money(r.estimated_impact_pounds)})`
          : "";
      blocks.push(`${r.area}${impact}.\n${r.scenario}`);
    }

    for (const o of live(f.opportunities)) {
      const saving =
        o.estimated_saving_pounds !== null
          ? ` (we estimate this could save ~${cur.money(o.estimated_saving_pounds)})`
          : "";
      blocks.push(`${o.area}${saving}.\n${o.how}`);
    }

    return blocks.join("\n\n");
  };

  // Group rows by section, preserving the canonical section order.
  // Any unknown section name falls through to "other" rather than
  // getting silently dropped.
  const rowsBySection = new Map<string, EpcCostRow[]>();
  for (const row of rows) {
    const key = row.section ?? "other";
    const bucket = rowsBySection.get(key) ?? [];
    bucket.push(row);
    rowsBySection.set(key, bucket);
  }

  // Title row carries the project name; row index 0 stays a single
  // string so a merge across A:F renders as a title banner.
  const titleRow: (string | number | null)[] = [
    `Procurement review - ${projectName}`,
    null,
    null,
    null,
    null,
    null,
  ];

  type RowKind = "title" | "blank" | "columnHeader" | "sectionHeader" | "item" | "total";

  const aoa: (string | number | null)[][] = [titleRow, []];
  const rowKinds: RowKind[] = ["title", "blank"];
  // Track which rows we'll horizontally merge across A:F (title,
  // section headers).
  const mergeRows: number[] = [0];

  aoa.push(HEADER_ROW);
  rowKinds.push("columnHeader");

  // Section-by-section build-up. Each section: header row (merged)
  // then one row per line item. Sections with zero rows are skipped
  // so the EPC doesn't see empty section banners.
  let anySectionRendered = false;

  // Sections come in the canonical order; any section the BE labels
  // that isn't in the order list lands at the end under its raw key
  // (defensive against future taxonomy additions).
  const seenSections = new Set<string>();
  const sectionsToRender: string[] = [];
  for (const s of SECTION_ORDER) {
    if (rowsBySection.has(s)) {
      sectionsToRender.push(s);
      seenSections.add(s);
    }
  }
  for (const s of rowsBySection.keys()) {
    if (!seenSections.has(s)) sectionsToRender.push(s);
  }

  for (const section of sectionsToRender) {
    const sectionRows = rowsBySection.get(section) ?? [];
    if (sectionRows.length === 0) continue;
    anySectionRendered = true;

    // Section header row (display name in col A, merged across).
    const headerIdx = aoa.length;
    aoa.push([SECTION_DISPLAY[section] ?? section, null, null, null, null, null]);
    rowKinds.push("sectionHeader");
    mergeRows.push(headerIdx);

    for (const row of sectionRows) {
      aoa.push([
        null,
        row.label,
        row.total_cost ?? null,
        row.per_kwp ?? null,
        row.comment ?? "",
        buildCommentary(grouped.byRow.get(row.label)),
      ]);
      rowKinds.push("item");
    }
  }

  // Grand TOTAL row at the bottom (no per-section subtotals - those
  // were dropped on Noaa's earlier feedback and stay dropped). Sums
  // every rendered line item's total_cost; per_kwp is the £/kWp
  // average we'd otherwise lose if we just dropped the row entirely.
  // Operators reading the xlsx need a footing line to anchor the
  // headline number on (2026-06-09 ask).
  if (anySectionRendered) {
    let grandTotal = 0;
    let grandPerKwp = 0;
    for (const section of sectionsToRender) {
      for (const row of rowsBySection.get(section) ?? []) {
        if (row.total_cost != null) grandTotal += row.total_cost;
        if (row.per_kwp != null) grandPerKwp += row.per_kwp;
      }
    }
    aoa.push([
      "TOTAL",
      "",
      grandTotal || null,
      grandPerKwp || null,
      "",
      "",
    ]);
    rowKinds.push("total");
  }

  if (!anySectionRendered) {
    aoa.push([
      null,
      "We didn't find any cost rows on this assessment.",
      null,
      null,
      "",
      "",
    ]);
    rowKinds.push("item");
  }

  const ws = XLSXStyle.utils.aoa_to_sheet(aoa);
  ws["!cols"] = colWidths;

  // Merge title + section header rows across all six columns so they
  // render as banners. The xlsx merge spec is {s:{r,c}, e:{r,c}}.
  ws["!merges"] = mergeRows.map((r) => ({
    s: { r, c: 0 },
    e: { r, c: 5 },
  }));

  // Style palettes.
  const titleStyle = {
    font: { bold: true, color: { rgb: "FFFFFF" }, sz: 14 },
    fill: { fgColor: { rgb: "00022C" } },
    alignment: { vertical: "center", horizontal: "left", wrapText: true },
  };
  const columnHeaderStyle = {
    font: { bold: true, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: "00022C" } },
    alignment: { wrapText: true, vertical: "center", horizontal: "left" },
    border: { bottom: { style: "thin", color: { rgb: "CCCCCC" } } },
  };
  const sectionHeaderStyle = {
    font: { bold: true, color: { rgb: "00022C" } },
    fill: { fgColor: { rgb: "E8E9ED" } },
    alignment: { wrapText: true, vertical: "center", horizontal: "left" },
  };
  const bodyStyle = {
    alignment: { wrapText: true, vertical: "top", horizontal: "left" },
  };
  const numberStyle = {
    alignment: { wrapText: true, vertical: "top", horizontal: "right" },
    numFmt: moneyFmt0,
  };
  const perKwpStyle = {
    alignment: { wrapText: true, vertical: "top", horizontal: "right" },
    numFmt: moneyFmt2,
  };
  // Grand TOTAL row at the bottom - navy band that matches the title
  // banner so the operator's eye knows where the footing line is.
  const totalLabelStyle = {
    font: { bold: true, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: "00022C" } },
    alignment: { wrapText: true, vertical: "center", horizontal: "left" },
  };
  const totalNumberStyle = {
    font: { bold: true, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: "00022C" } },
    alignment: { wrapText: true, vertical: "center", horizontal: "right" },
    numFmt: moneyFmt0,
  };
  const totalPerKwpStyle = {
    font: { bold: true, color: { rgb: "FFFFFF" } },
    fill: { fgColor: { rgb: "00022C" } },
    alignment: { wrapText: true, vertical: "center", horizontal: "right" },
    numFmt: moneyFmt2,
  };

  const range = XLSXStyle.utils.decode_range(ws["!ref"] ?? "A1");
  for (let R = range.s.r; R <= range.e.r; R++) {
    const kind = rowKinds[R];
    for (let C = range.s.c; C <= range.e.c; C++) {
      const addr = XLSXStyle.utils.encode_cell({ r: R, c: C });
      // For full-row banner kinds (title / columnHeader / sectionHeader /
      // total) the fill has to paint across EVERY column or the band
      // shows white gaps where ``aoa_to_sheet`` skipped null / empty
      // cells. Materialise a blank cell first so the styling pass below
      // has something to attach the fill to. For ``item`` rows we still
      // skip empties - we don't want to apply numFmt to a non-existent
      // value cell, and the body style is no-fill anyway.
      if (!ws[addr]) {
        if (kind === "title" || kind === "columnHeader" || kind === "sectionHeader" || kind === "total") {
          ws[addr] = { t: "s", v: "" };
        } else {
          continue;
        }
      }
      if (kind === "title") {
        ws[addr].s = titleStyle;
      } else if (kind === "columnHeader") {
        ws[addr].s = columnHeaderStyle;
      } else if (kind === "sectionHeader") {
        ws[addr].s = sectionHeaderStyle;
      } else if (kind === "item") {
        ws[addr].s = C === 2 ? numberStyle : C === 3 ? perKwpStyle : bodyStyle;
      } else if (kind === "total") {
        ws[addr].s =
          C === 2 ? totalNumberStyle : C === 3 ? totalPerKwpStyle : totalLabelStyle;
      }
    }
  }

  // Freeze title + blank + column header so the column labels stay
  // visible when the operator scrolls down.
  ws["!freeze"] = { xSplit: "0", ySplit: "3" };

  const wb = XLSXStyle.utils.book_new();
  XLSXStyle.utils.book_append_sheet(wb, ws, "Procurement");

  // Filename: just the project display name + the export type. No
  // UUID suffix - operators find files by project name, not by id.
  // Sanitise everything for filesystem safety (spaces stay, but
  // slashes / control chars get stripped).
  // eslint-disable-next-line no-control-regex
  const FILENAME_UNSAFE = /[<>:"/\\|?*\x00-\x1f]/g;
  const sanitiseForFilename = (s: string) =>
    s.replace(FILENAME_UNSAFE, "_").trim() || "project";
  const safeProjectName = sanitiseForFilename(projectName);
  XLSXStyle.writeFile(wb, `${safeProjectName} procurement assessment.xlsx`);
};

const SummarizeForEpcButton = ({
  rows,
  grouped,
  projectName,
}: {
  rows: EpcCostRow[];
  grouped: ReturnType<typeof groupFindingsByRow>;
  projectName: string;
}) => {
  const cur = useCurrency();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const summary = open ? composeEpcSummary(rows, grouped, cur) : "";

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(summary);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const onDownload = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      await buildEpcXlsx(rows, grouped, projectName, cur);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error("buildEpcXlsx failed", err);
      setDownloadError(msg);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className={styles.secondaryButton}
        onClick={() => setOpen(true)}
      >
        Summarise for EPC
      </button>
      {open && (
        <div className={styles.dialogOverlay} onClick={() => setOpen(false)}>
          <div
            className={styles.dialog}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className={styles.dialogHeader}>
              <h3 className={styles.dialogTitle}>Summary for EPC follow-up</h3>
              <button
                type="button"
                className={styles.dialogClose}
                onClick={() => setOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <p className={styles.dialogBody}>
              A line-by-line note we can send to the EPC, written as if
              we're talking to them directly. Covers every row where
              we'd like to confirm something, flag a risk, point at a
              possible saving, or talk through how the cost lines up
              with what's on site. Copy and paste straight into an
              email.
            </p>
            <textarea
              className={styles.dialogTextarea}
              value={summary}
              readOnly
              spellCheck={false}
            />
            {downloadError && (
              <div className={styles.runError}>
                Download failed: {downloadError}
              </div>
            )}
            <div className={styles.dialogFooter}>
              <button
                type="button"
                className={styles.runButton}
                onClick={onCopy}
              >
                {copied ? "Copied" : "Copy to clipboard"}
              </button>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={onDownload}
                disabled={downloading}
              >
                {downloading ? "Building xlsx…" : "Download as xlsx"}
              </button>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => setOpen(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Re-export ProcurementFindings to satisfy editor jump-to-definition users.
export type { ProcurementFindings };
